'use client';
import createCache from '@emotion/cache';
import { unstable_ClassNameGenerator } from '@mui/material/className';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { TssCacheProvider } from 'tss-react';

import { themeFactory } from '../theme';

// Dedicated emotion cache for tss-react with a distinct key so its styles are
// always injected into a separate <style> tag that follows MUI's injectFirst
// styles. Without this, both systems share the same prepend:true cache and the
// injection order becomes non-deterministic across SPA navigations, causing
// intermittent style bugs (e.g. wrong Card padding / Panel size).
const tssCache = createCache({ key: 'tss' });

/**
 * IDSThemeProvider component for applying IDS themes to your application.
 *
 * @param {Object} props - Component props
 * @param {string} [props.prefix] - Optional prefix for generated class names
 * @param {string} [props.themeName='IDS'] - Name of the theme to apply
 * @param {boolean} [props.enableProximaNova] - @deprecated Temporary prop for Proxima Nova font support. This prop will be removed after April 2026.
 */
export const IDSThemeProvider = ({ prefix, themeName = 'IDS', enableProximaNova, ...rest }) => {
  if (prefix) {
    const random_no = Math.round(Math.random() * Number.MAX_SAFE_INTEGER);
    const seed = `${prefix}-${random_no}`;

    unstable_ClassNameGenerator.configure((componentName) => `${seed}-${componentName}`);
  }

  const legacyThemes = ['IDS', 'IDSDark', 'patientDark', 'patientLight', 'wireframe'];
  const supportsLegacy = legacyThemes.includes(themeName);
  const resolvedThemeName = enableProximaNova && supportsLegacy ? `${themeName}Legacy` : themeName;

  return (
    <StyledEngineProvider injectFirst>
      <TssCacheProvider value={tssCache}>
        <ThemeProvider theme={themeFactory(resolvedThemeName, enableProximaNova)} {...rest} />
      </TssCacheProvider>
    </StyledEngineProvider>
  );
};

export const IDSThemeWithPrefix = IDSThemeProvider;

export default IDSThemeProvider;
